import { html, nothing } from "lit";
import type {
  LiveSessionCreateInput,
  LiveSessionDetail,
  LiveSessionRow,
} from "../controllers/live-sessions.ts";
import { formatAgo } from "../format.ts";
import {
  describeSessionActivity,
  formatDuration,
  formatPollInterval,
  humanPresenceLabel,
  phaseLabel,
  statusLabel,
} from "./live-sessions-presenter.ts";

export type LiveSessionsPanelProps = {
  loading: boolean;
  sessions: LiveSessionRow[];
  error: string | null;
  onRefresh: () => void;
  selectedSessionId?: string | null;
  selectedDetail?: LiveSessionDetail | null;
  detailLoading?: boolean;
  onSelectSession?: (sessionId: string | null) => void;
  employeeOptions?: Array<{ id: string; label: string }>;
  observerAgentOptions?: Array<{ id: string; label: string }>;
  desktopNodeOptions?: Array<{ url: string; label: string }>;
  onCreateSession?: (input: LiveSessionCreateInput) => void;
  onStartSession?: (sessionId: string) => void;
  onPauseSession?: (sessionId: string) => void;
  onResumeSession?: (sessionId: string) => void;
  onEndSession?: (sessionId: string) => void;
};

function statusModifier(status: string): string {
  switch (status) {
    case "active":
      return "live-session-card--active";
    case "idle":
      return "live-session-card--idle";
    case "paused":
      return "live-session-card--paused";
    case "ended":
    case "aborted":
      return "live-session-card--ended";
    default:
      return "live-session-card--pending";
  }
}

function badgeClass(status: string): string {
  if (status === "active") {
    return "live-session-badge live-session-badge--active";
  }
  if (status === "idle") {
    return "live-session-badge live-session-badge--idle";
  }
  return "live-session-badge";
}

function renderSkillChips(session: LiveSessionRow) {
  const focus = session.focusedWindow?.trim();
  const skills = session.recentSkills ?? [];
  if (!focus && skills.length === 0) {
    return html`
      <p class="muted" style="font-size: 12px; margin: 0">No applications recorded yet.</p>
    `;
  }
  const chips: Array<{ label: string; focus?: boolean }> = [];
  if (focus) {
    chips.push({ label: focus, focus: true });
  }
  for (const name of skills) {
    if (focus && name === focus) {
      continue;
    }
    chips.push({ label: name });
  }
  return html`
    <div class="live-session-skills">
      ${chips.slice(0, 10).map(
        (c) => html`
          <span
            class="live-session-skill-chip ${c.focus ? "live-session-skill-chip--focus" : ""}"
            title=${c.label}
            >${c.focus ? "Focus: " : ""}${c.label}</span
          >
        `,
      )}
    </div>
  `;
}

function renderSessionActions(session: LiveSessionRow, props: LiveSessionsPanelProps) {
  const id = session.sessionId;
  const buttons = [];
  if (session.status === "pending" && props.onStartSession) {
    buttons.push(html`
      <button type="button" class="btn primary" @click=${(e: Event) => {
        e.stopPropagation();
        props.onStartSession?.(id);
      }}>Start</button>
    `);
  }
  if ((session.status === "active" || session.status === "idle") && props.onPauseSession) {
    buttons.push(html`
      <button type="button" class="btn" @click=${(e: Event) => {
        e.stopPropagation();
        props.onPauseSession?.(id);
      }}>Pause</button>
    `);
  }
  if (session.status === "paused" && props.onResumeSession) {
    buttons.push(html`
      <button type="button" class="btn primary" @click=${(e: Event) => {
        e.stopPropagation();
        props.onResumeSession?.(id);
      }}>Resume</button>
    `);
  }
  if (session.status !== "ended" && session.status !== "aborted" && props.onEndSession) {
    buttons.push(html`
      <button type="button" class="btn danger" @click=${(e: Event) => {
        e.stopPropagation();
        props.onEndSession?.(id);
      }}>End</button>
    `);
  }
  if (buttons.length === 0) {
    return nothing;
  }
  return html`<div class="live-session-card__actions">${buttons}</div>`;
}

function renderCreateForm(props: LiveSessionsPanelProps) {
  if (!props.onCreateSession) {
    return nothing;
  }
  const employees = props.employeeOptions ?? [];
  const observers = props.observerAgentOptions ?? [];
  const nodes = props.desktopNodeOptions ?? [];
  return html`
    <form
      class="live-sessions-create"
      @submit=${(e: Event) => {
        e.preventDefault();
        const form = e.currentTarget as HTMLFormElement;
        const data = new FormData(form);
        const targetEmployeeId = String(data.get("targetEmployeeId") ?? "").trim();
        const observerAgentId = String(data.get("observerAgentId") ?? "").trim();
        const desktopControllerUrl = String(data.get("desktopControllerUrl") ?? "").trim();
        const sampleIntervalMs = Number(data.get("sampleIntervalMs") ?? 500);
        if (!targetEmployeeId || !observerAgentId) {
          return;
        }
        props.onCreateSession?.({
          targetEmployeeId,
          observerAgentId,
          desktopControllerUrl: desktopControllerUrl || undefined,
          sampleIntervalMs: Number.isFinite(sampleIntervalMs) ? sampleIntervalMs : 500,
        });
        form.reset();
      }}
    >
      <h3 class="live-sessions-create__title">Start live observation</h3>
      <div class="live-sessions-create__grid">
        <label>
          <span>Employee</span>
          <select name="targetEmployeeId" required>
            <option value="">Select employee…</option>
            ${employees.map((e) => html`<option value=${e.id}>${e.label}</option>`)}
          </select>
        </label>
        <label>
          <span>Observer agent</span>
          <select name="observerAgentId" required>
            <option value="">Select agent…</option>
            ${observers.map((a) => html`<option value=${a.id}>${a.label}</option>`)}
          </select>
        </label>
        <label>
          <span>Desktop controller (optional)</span>
          <select name="desktopControllerUrl">
            <option value="">Auto from connected desktop node</option>
            ${nodes.map((n) => html`<option value=${n.url}>${n.label}</option>`)}
          </select>
        </label>
        <label>
          <span>Poll interval (ms)</span>
          <input name="sampleIntervalMs" type="number" min="100" step="100" value="500" />
        </label>
      </div>
      <button type="submit" class="btn primary">Create session</button>
    </form>
  `;
}

function renderSessionCard(session: LiveSessionRow, props: LiveSessionsPanelProps) {
  const selected = props.selectedSessionId === session.sessionId;
  const detail = selected ? props.selectedDetail : null;
  const presence = humanPresenceLabel(session);
  const observerLine =
    session.observerAgentId != null
      ? session.observerBound
        ? session.observerAgentId
        : `${session.observerAgentId} (binding…)`
      : "—";

  return html`
    <article
      class="live-session-card ${statusModifier(session.status)} ${selected ? "live-session-card--selected" : ""}"
      data-live-session-id=${session.sessionId}
      data-testid="live-session-row"
      @click=${() => props.onSelectSession?.(selected ? null : session.sessionId)}
    >
      <div class="live-session-card__head">
        <span class="live-session-card__status-dot" aria-hidden="true"></span>
        <div class="live-session-card__main">
          <div class="live-session-card__title-row">
            <span class="live-session-card__employee">${session.targetEmployeeId}</span>
            <div class="live-session-card__badges">
              <span class=${badgeClass(session.status)}>${statusLabel(session.status)}</span>
              <span class="live-session-badge">${phaseLabel(session.phase)}</span>
              ${
                presence
                  ? html`<span class="live-session-badge live-session-badge--human">${presence}</span>`
                  : nothing
              }
            </div>
          </div>
          ${
            session.desktopEndpoint
              ? html`
                <p class="live-session-card__endpoint">
                  Worker desktop · <strong>${session.desktopEndpoint}</strong>
                  ${
                    session.observationMode
                      ? html` · ${session.observationMode} @ ${formatPollInterval(session.sampleIntervalMs)}`
                      : nothing
                  }
                </p>
              `
              : html`
                  <p class="live-session-card__endpoint muted">Desktop controller URL not configured</p>
                `
          }
        </div>
        <button
          type="button"
          class="live-session-card__toggle"
          @click=${(e: Event) => {
            e.stopPropagation();
            props.onSelectSession?.(selected ? null : session.sessionId);
          }}
        >
          ${selected ? "Hide details" : "Details"}
        </button>
      </div>

      <p class="live-session-card__narrative">${describeSessionActivity(session)}</p>

      <div class="live-session-card__stats">
        <div class="live-session-stat">
          <div class="live-session-stat__label">Last poll</div>
          <div class="live-session-stat__value">${formatAgo(Date.parse(session.lastActivityAt))}</div>
        </div>
        <div class="live-session-stat">
          <div class="live-session-stat__label">Duration</div>
          <div class="live-session-stat__value">${formatDuration(session.durationMinutes)}</div>
        </div>
        <div class="live-session-stat">
          <div class="live-session-stat__label">Apps seen</div>
          <div class="live-session-stat__value">${session.skillCount}</div>
        </div>
        <div class="live-session-stat">
          <div class="live-session-stat__label">Windows now</div>
          <div class="live-session-stat__value">
            ${session.windowCount != null ? session.windowCount : "—"}
          </div>
        </div>
      </div>

      <div class="live-session-card__skills">
        <div class="live-session-card__skills-label">Desktop activity</div>
        ${renderSkillChips(session)}
      </div>

      <dl class="live-session-card__meta">
        <div><dt>Observer</dt><dd>${observerLine}</dd></div>
        <div><dt>Session</dt><dd>${session.sessionId}</dd></div>
      </dl>

      ${renderSessionActions(session, props)}

      ${
        selected
          ? html`
            <div class="live-session-card__detail">
              ${
                props.detailLoading
                  ? html`
                      <p class="muted">Loading session detail…</p>
                    `
                  : detail
                    ? html`
                      ${
                        detail.config?.desktopControllerUrl
                          ? html`
                            <p class="muted" style="font-size: 12px; margin: 0 0 8px;">
                              Controller:
                              <code>${detail.config.desktopControllerUrl}</code>
                            </p>
                          `
                          : nothing
                      }
                      ${
                        detail.observerSubagent
                          ? html`
                            <p class="muted" style="font-size: 12px; margin: 0 0 8px;">
                              Subagent session:
                              <code>${detail.observerSubagent.childSessionKey}</code>
                            </p>
                          `
                          : nothing
                      }
                      ${
                        detail.skills && detail.skills.length > 0
                          ? html`
                            <p class="muted" style="font-size: 11px; margin: 0 0 6px;">All recorded skills</p>
                            <pre>${detail.skills
                              .map((s) => `• ${s.name} (${s.category})`)
                              .join("\n")}</pre>
                          `
                          : html`
                              <p class="muted" style="font-size: 12px">No skills stored on this session.</p>
                            `
                      }
                    `
                    : nothing
              }
            </div>
          `
          : nothing
      }
    </article>
  `;
}

export function renderLiveSessionsPanel(props: LiveSessionsPanelProps) {
  const { sessions, loading, error } = props;
  const activeCount = sessions.filter((s) => s.status === "active" || s.status === "idle").length;

  return html`
    <section class="live-sessions-panel" data-testid="live-sessions-panel">
      <div class="live-sessions-panel__header">
        <div>
          <h2 class="live-sessions-panel__title">Live worker sessions</h2>
          <p class="live-sessions-panel__intro">
            Real-time observation of an employee desktop via
            <strong>prizm-desktop-controller</strong>. The gateway polls the worker machine, records
            open applications, and streams summaries to an observer agent that learns repeatable
            workflows.
          </p>
        </div>
        <div class="live-sessions-panel__actions">
          ${
            activeCount > 0
              ? html`
                  <span class="live-sessions-panel__poll-hint">Auto-refresh 5s</span>
                `
              : nothing
          }
          <button class="btn" ?disabled=${loading} @click=${props.onRefresh}>Refresh</button>
        </div>
      </div>

      ${error ? html`<div class="callout danger" style="margin-bottom: 14px;">${error}</div>` : nothing}

      ${renderCreateForm(props)}

      ${
        loading && sessions.length === 0
          ? html`
              <p class="muted">Loading live sessions…</p>
            `
          : nothing
      }

      ${
        !loading && sessions.length === 0
          ? html`
              <div class="live-sessions-empty">
                <p class="live-sessions-empty__title">No live sessions</p>
                <p class="live-sessions-empty__body">
                  Create a session below, or use the gateway API with a worker
                  <code>desktopControllerUrl</code>, then start it to begin monitoring.
                </p>
              </div>
            `
          : html`
            <div class="live-sessions-list">
              ${sessions.map((s) => renderSessionCard(s, props))}
            </div>
          `
      }
    </section>
  `;
}
